import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { WorkloadViewMode } from '../../domain/enums/workload-view-mode.enum';
import { RebalancingProposalEntity } from '../../domain/entities/rebalancing-proposal.entity';
import { RebalancingProposalGroupEntity } from '../../domain/entities/rebalancing-proposal-group.entity';
import { RebalancingProposalMember } from '../../domain/entities/rebalancing-proposal-member.entity';
import { TeamBalancingCandidateEntity } from '../../domain/entities/team-balancing-candidate.entity';
import { TeamWorkloadEntity } from '../../domain/entities/team-workload.entity';
import { TeamWorkloadSummaryEntity } from '../../domain/entities/team-workload-summary.entity';
import { TimeOffInfoEntity } from '../../domain/entities/time-off-info.entity';
import { WorkloadCaseEntity } from '../../domain/entities/workload-case.entity';
import { WorkloadMemberEntity } from '../../domain/entities/workload-member.entity';
import { ProposalRemovalReason } from '../../domain/enums/proposal-removal-reason.enum';

interface RawTimeOff { id: string; type: string; startDate: string; endDate: string; durationDays: number; notes: string }
interface RawCase { caseId: string; caseNumber: string; caseTypeName: string; overdueText: string; overdueDays: number; status: string; detailUrl: string }
interface RawMember { userId: string; firstName: string; lastName: string; role: string; email: string; phone: string; teams: string[]; activeCasesCount: number; capacity: number; utilizationPercent: number; overdueCount: number; highPriorityCount: number; closedThisMonthCount: number; isOverCapacity: boolean; closedHiddenCount: number; timeOff: RawTimeOff | null; cases: RawCase[] }
interface RawTeam { teamId: string; teamName: string; managedBy: string[]; overCapacityCount: number; overdueCount: number; onTimeOffCount: number; totalMembers: number; members: RawMember[] }

interface RawProposalMember {
  userId: string;
  displayName: string;
  currentCases: number;
  capacity: number;
  utilizationPercent: number;
}

interface RawProposal {
  caseId: string;
  caseNumber: string;
  caseTypeName: string;
  caseStatusLabel: string;
  isOverdue: boolean;
  teamId: string;
  teamName: string;
  fromMember: RawProposalMember;
  toMember: RawProposalMember;
  alternates: RawProposalMember[];
  rationale: string;
}

function _mapTimeOff(raw: RawTimeOff | null): TimeOffInfoEntity | null {
  if (!raw) return null;
  const e = new TimeOffInfoEntity();
  e.id = raw.id;
  e.type = raw.type;
  e.startDate = raw.startDate;
  e.endDate = raw.endDate;
  e.durationDays = raw.durationDays;
  e.notes = raw.notes;
  return e;
}

function _mapCase(raw: RawCase): WorkloadCaseEntity {
  const e = new WorkloadCaseEntity();
  e.caseId = raw.caseId;
  e.caseNumber = raw.caseNumber;
  e.caseTypeName = raw.caseTypeName;
  e.overdueText = raw.overdueText;
  e.overdueDays = raw.overdueDays;
  e.status = raw.status;
  e.detailUrl = raw.detailUrl;
  return e;
}

function _mapMember(raw: RawMember): WorkloadMemberEntity {
  const e = new WorkloadMemberEntity();
  e.userId = raw.userId;
  e.firstName = raw.firstName;
  e.lastName = raw.lastName;
  e.role = raw.role;
  e.email = raw.email;
  e.phone = raw.phone;
  e.teams = raw.teams;
  e.activeCasesCount = raw.activeCasesCount;
  e.capacity = raw.capacity;
  e.utilizationPercent = raw.utilizationPercent;
  e.overdueCount = raw.overdueCount;
  e.highPriorityCount = raw.highPriorityCount;
  e.closedThisMonthCount = raw.closedThisMonthCount;
  e.isOverCapacity = raw.isOverCapacity;
  e.closedHiddenCount = raw.closedHiddenCount;
  e.timeOff = _mapTimeOff(raw.timeOff);
  e.cases = raw.cases.map(_mapCase);
  return e;
}

function _mapTeam(raw: RawTeam): TeamWorkloadEntity {
  const e = new TeamWorkloadEntity();
  e.teamId = raw.teamId;
  e.teamName = raw.teamName;
  e.managedBy = raw.managedBy;
  e.overCapacityCount = raw.overCapacityCount;
  e.overdueCount = raw.overdueCount;
  e.onTimeOffCount = raw.onTimeOffCount;
  e.totalMembers = raw.totalMembers;
  e.members = raw.members.map(_mapMember);
  return e;
}

function _mapProposalMember(raw: RawProposalMember): RebalancingProposalMember {
  const e = new RebalancingProposalMember();
  e.userId = raw.userId;
  e.displayName = raw.displayName;
  e.currentCases = raw.currentCases;
  e.capacity = raw.capacity;
  e.utilizationPercent = raw.utilizationPercent;
  return e;
}

function _mapProposal(raw: RawProposal): RebalancingProposalEntity {
  const e = new RebalancingProposalEntity();
  e.caseId = raw.caseId;
  e.caseNumber = raw.caseNumber;
  e.caseTypeName = raw.caseTypeName;
  e.caseStatusLabel = raw.caseStatusLabel;
  e.isOverdue = raw.isOverdue;
  e.teamId = raw.teamId;
  e.teamName = raw.teamName;
  e.fromMember = _mapProposalMember(raw.fromMember);
  e.toMember = _mapProposalMember(raw.toMember);
  e.alternates = raw.alternates.map(_mapProposalMember);
  e.rationale = raw.rationale;
  return e;
}

function _groupProposalsByTeam(proposals: RebalancingProposalEntity[]): RebalancingProposalGroupEntity[] {
  const map = new Map<string, RebalancingProposalGroupEntity>();
  for (const p of proposals) {
    if (!map.has(p.teamId)) {
      const g = new RebalancingProposalGroupEntity();
      g.teamId = p.teamId;
      g.teamName = p.teamName;
      g.proposals = [];
      map.set(p.teamId, g);
    }
    map.get(p.teamId)!.proposals.push(p);
  }
  return Array.from(map.values());
}

const OVER_CAPACITY_MEMBERS: RawMember[] = [
  {
    userId: 'hr-016', firstName: 'Olivia', lastName: 'Reyes', role: 'HR Agent',
    email: 'olivia.reyes@company.gov', phone: '(555) 201-4176', teams: ['Payroll & Compensation'],
    activeCasesCount: 3, capacity: 2, utilizationPercent: 150, overdueCount: 3,
    highPriorityCount: 0, closedThisMonthCount: 0,
    isOverCapacity: true, closedHiddenCount: 0,
    timeOff: { id: 'timeoff-010', type: 'Vacation', startDate: '2026-05-26', endDate: '2026-05-30', durationDays: 5, notes: 'Memorial Day week off' },
    cases: [
      { caseId: 'enc-ss-26050001', caseNumber: 'SS-26050001', caseTypeName: 'Paycheck / Salary Discrepancy', overdueText: '17d overdue', overdueDays: 17, status: 'In Progress', detailUrl: '/cases/detail/SS-26050001' },
      { caseId: 'enc-ss-26050002', caseNumber: 'SS-26050002', caseTypeName: 'Tax Withholding (W-4)', overdueText: '4d overdue', overdueDays: 4, status: 'In Progress', detailUrl: '/cases/detail/SS-26050002' },
      { caseId: 'enc-ss-26050003', caseNumber: 'SS-26050003', caseTypeName: 'Direct Deposit Update', overdueText: '23d overdue', overdueDays: 23, status: 'New', detailUrl: '/cases/detail/SS-26050003' },
    ],
  },
  {
    userId: 'hr-017', firstName: 'Nathan', lastName: 'Brooks', role: 'HR Agent',
    email: 'nathan.brooks@company.gov', phone: '(555) 202-5287', teams: ['Payroll & Compensation'],
    activeCasesCount: 3, capacity: 2, utilizationPercent: 150, overdueCount: 3,
    highPriorityCount: 1, closedThisMonthCount: 0,
    isOverCapacity: true, closedHiddenCount: 0,
    timeOff: { id: 'timeoff-011', type: 'Vacation', startDate: '2026-06-08', endDate: '2026-06-12', durationDays: 5, notes: '' },
    cases: [
      { caseId: 'enc-ss-26050004', caseNumber: 'SS-26050004', caseTypeName: 'Paycheck / Salary Discrepancy', overdueText: '17d overdue', overdueDays: 17, status: 'In Progress', detailUrl: '/cases/detail/SS-26050004' },
      { caseId: 'enc-ss-26050005', caseNumber: 'SS-26050005', caseTypeName: 'Tax Withholding (W-4)', overdueText: '4d overdue', overdueDays: 4, status: 'In Progress', detailUrl: '/cases/detail/SS-26050005' },
      { caseId: 'enc-ss-26050006', caseNumber: 'SS-26050006', caseTypeName: 'Direct Deposit Update', overdueText: '23d overdue', overdueDays: 23, status: 'New', detailUrl: '/cases/detail/SS-26050006' },
    ],
  },
  {
    userId: 'hr-018', firstName: 'Sofia', lastName: 'Alvarez', role: 'HR Agent',
    email: 'sofia.alvarez@company.gov', phone: '(555) 203-6398', teams: ['Payroll & Compensation'],
    activeCasesCount: 3, capacity: 2, utilizationPercent: 150, overdueCount: 3,
    highPriorityCount: 0, closedThisMonthCount: 2,
    isOverCapacity: true, closedHiddenCount: 0,
    timeOff: { id: 'timeoff-012', type: 'Vacation', startDate: '2026-06-15', endDate: '2026-06-19', durationDays: 5, notes: '' },
    cases: [
      { caseId: 'enc-ss-26050007', caseNumber: 'SS-26050007', caseTypeName: 'Paycheck / Salary Discrepancy', overdueText: '17d overdue', overdueDays: 17, status: 'In Progress', detailUrl: '/cases/detail/SS-26050007' },
      { caseId: 'enc-ss-26050008', caseNumber: 'SS-26050008', caseTypeName: 'Tax Withholding (W-4)', overdueText: '4d overdue', overdueDays: 4, status: 'In Progress', detailUrl: '/cases/detail/SS-26050008' },
      { caseId: 'enc-ss-26050009', caseNumber: 'SS-26050009', caseTypeName: 'Direct Deposit Update', overdueText: '23d overdue', overdueDays: 23, status: 'New', detailUrl: '/cases/detail/SS-26050009' },
    ],
  },
  {
    userId: 'hr-019', firstName: 'Henry', lastName: 'Walsh', role: 'HR Agent',
    email: 'henry.walsh@company.gov', phone: '(555) 204-7419', teams: ['Payroll & Compensation'],
    activeCasesCount: 3, capacity: 2, utilizationPercent: 150, overdueCount: 3,
    highPriorityCount: 0, closedThisMonthCount: 1,
    isOverCapacity: true, closedHiddenCount: 0,
    timeOff: { id: 'timeoff-013', type: 'Vacation', startDate: '2026-06-22', endDate: '2026-06-22', durationDays: 1, notes: '' },
    cases: [
      { caseId: 'enc-ss-26050010', caseNumber: 'SS-26050010', caseTypeName: 'Paycheck / Salary Discrepancy', overdueText: '17d overdue', overdueDays: 17, status: 'In Progress', detailUrl: '/cases/detail/SS-26050010' },
      { caseId: 'enc-ss-26050011', caseNumber: 'SS-26050011', caseTypeName: 'Tax Withholding (W-4)', overdueText: '4d overdue', overdueDays: 4, status: 'In Progress', detailUrl: '/cases/detail/SS-26050011' },
      { caseId: 'enc-ss-26050012', caseNumber: 'SS-26050012', caseTypeName: 'Direct Deposit Update', overdueText: '23d overdue', overdueDays: 23, status: 'New', detailUrl: '/cases/detail/SS-26050012' },
    ],
  },
  {
    userId: 'hr-002', firstName: 'David', lastName: 'Kim', role: 'Manager',
    email: 'david.kim@company.gov', phone: '(555) 205-8530', teams: ['Benefits & Leave'],
    activeCasesCount: 5, capacity: 4, utilizationPercent: 125, overdueCount: 5,
    highPriorityCount: 2, closedThisMonthCount: 3,
    isOverCapacity: true, closedHiddenCount: 0,
    timeOff: { id: 'timeoff-001', type: 'Vacation', startDate: '2026-05-22', endDate: '2026-05-24', durationDays: 2, notes: '' },
    cases: [
      { caseId: 'enc-ss-25110017', caseNumber: 'SS-25110017', caseTypeName: 'Paycheck / Salary Discrepancy', overdueText: '39d overdue', overdueDays: 39, status: 'Assigned', detailUrl: '/cases/detail/SS-25110017' },
      { caseId: 'enc-ss-25110014', caseNumber: 'SS-25110014', caseTypeName: 'FMLA / Leave of Absence', overdueText: '38d overdue', overdueDays: 38, status: 'Assigned', detailUrl: '/cases/detail/SS-25110014' },
      { caseId: 'enc-ss-25110012', caseNumber: 'SS-25110012', caseTypeName: 'General HR Question', overdueText: '32d overdue', overdueDays: 32, status: 'Assigned', detailUrl: '/cases/detail/SS-25110012' },
      { caseId: 'enc-ss-25110022', caseNumber: 'SS-25110022', caseTypeName: 'Medical / Dental / Vision', overdueText: '17d overdue', overdueDays: 17, status: 'New', detailUrl: '/cases/detail/SS-25110022' },
      { caseId: 'enc-ss-25110006', caseNumber: 'SS-25110006', caseTypeName: 'Direct Deposit', overdueText: '21h overdue', overdueDays: 0, status: 'Assigned', detailUrl: '/cases/detail/SS-25110006' },
    ],
  },
  {
    // utilizationPercent 50% with capacity 4 — included here because of overdue cases, not over-capacity
    userId: 'hr-020', firstName: 'Jonah', lastName: 'Pierce', role: 'HR Agent',
    email: 'jonah.pierce@company.gov', phone: '(555) 206-9641', teams: ['Payroll & Compensation'],
    activeCasesCount: 2, capacity: 4, utilizationPercent: 50, overdueCount: 2,
    highPriorityCount: 0, closedThisMonthCount: 0,
    isOverCapacity: false, closedHiddenCount: 0,
    timeOff: null,
    cases: [
      { caseId: 'enc-ss-26050013', caseNumber: 'SS-26050013', caseTypeName: 'Tax Withholding (W-4)', overdueText: '13d overdue', overdueDays: 13, status: 'In Progress', detailUrl: '/cases/detail/SS-26050013' },
      { caseId: 'enc-ss-26050022', caseNumber: 'SS-26050022', caseTypeName: 'Paycheck / Salary Discrepancy', overdueText: '28d overdue', overdueDays: 28, status: 'New', detailUrl: '/cases/detail/SS-26050022' },
    ],
  },
  {
    userId: 'hr-003', firstName: 'Jennifer', lastName: 'Martinez', role: 'Manager',
    email: 'jennifer.martinez@company.gov', phone: '(555) 207-0752', teams: ['Payroll & Compensation'],
    activeCasesCount: 3, capacity: 10, utilizationPercent: 30, overdueCount: 3,
    highPriorityCount: 1, closedThisMonthCount: 4,
    isOverCapacity: false, closedHiddenCount: 0,
    timeOff: { id: 'timeoff-002', type: 'Vacation', startDate: '2026-05-22', endDate: '2026-05-24', durationDays: 2, notes: '' },
    cases: [
      { caseId: 'enc-ss-25110018', caseNumber: 'SS-25110018', caseTypeName: 'FMLA / Leave of Absence', overdueText: '39d overdue', overdueDays: 39, status: 'New', detailUrl: '/cases/detail/SS-25110018' },
      { caseId: 'enc-ss-25110020', caseNumber: 'SS-25110020', caseTypeName: 'Work Schedule Change Request', overdueText: '37d overdue', overdueDays: 37, status: 'New', detailUrl: '/cases/detail/SS-25110020' },
      { caseId: 'enc-ss-25110019', caseNumber: 'SS-25110019', caseTypeName: 'Retirement Plan', overdueText: '17d overdue', overdueDays: 17, status: 'New', detailUrl: '/cases/detail/SS-25110019' },
    ],
  },
];

const OVERDUE_MEMBERS: RawMember[] = [
  {
    userId: 'hr-001', firstName: 'Maria', lastName: 'Santos', role: 'Manager',
    email: 'maria.santos@company.gov', phone: '(555) 208-1863', teams: ['Records & Onboarding'],
    activeCasesCount: 10, capacity: 10, utilizationPercent: 100, overdueCount: 10,
    highPriorityCount: 3, closedThisMonthCount: 5,
    isOverCapacity: false, closedHiddenCount: 0,
    timeOff: null,
    cases: [
      { caseId: 'enc-ss-25110010', caseNumber: 'SS-25110010', caseTypeName: 'General HR Question', overdueText: '42d overdue', overdueDays: 42, status: 'Assigned', detailUrl: '/cases/detail/SS-25110010' },
      { caseId: 'enc-ss-25110011', caseNumber: 'SS-25110011', caseTypeName: 'FMLA / Leave of Absence', overdueText: '41d overdue', overdueDays: 41, status: 'Assigned', detailUrl: '/cases/detail/SS-25110011' },
      { caseId: 'enc-ss-25110013', caseNumber: 'SS-25110013', caseTypeName: 'Paycheck / Salary Discrepancy', overdueText: '35d overdue', overdueDays: 35, status: 'In Progress', detailUrl: '/cases/detail/SS-25110013' },
    ],
  },
  ...OVER_CAPACITY_MEMBERS,
  {
    userId: 'hr-005', firstName: 'Thomas', lastName: 'Blake', role: 'HR Agent',
    email: 'thomas.blake@company.gov', phone: '(555) 209-2974', teams: ['Benefits & Leave'],
    activeCasesCount: 4, capacity: 12, utilizationPercent: 33, overdueCount: 4,
    highPriorityCount: 1, closedThisMonthCount: 2,
    isOverCapacity: false, closedHiddenCount: 0,
    timeOff: null,
    cases: [
      { caseId: 'enc-er-25110001', caseNumber: 'ER-25110001', caseTypeName: 'Resource Management', overdueText: '26d overdue', overdueDays: 26, status: 'In Progress', detailUrl: '/cases/detail/ER-25110001' },
      { caseId: 'enc-er-25110002', caseNumber: 'ER-25110002', caseTypeName: 'Whistleblower Complaint', overdueText: '25d overdue', overdueDays: 25, status: 'In Progress', detailUrl: '/cases/detail/ER-25110002' },
    ],
  },
  {
    userId: 'hr-007', firstName: 'James', lastName: 'Wilson', role: 'HR Agent',
    email: 'james.wilson@company.gov', phone: '(555) 210-3085', teams: ['Records & Onboarding'],
    activeCasesCount: 4, capacity: 8, utilizationPercent: 50, overdueCount: 4,
    highPriorityCount: 2, closedThisMonthCount: 1,
    isOverCapacity: false, closedHiddenCount: 0,
    timeOff: null,
    cases: [
      { caseId: 'enc-ss-25110030', caseNumber: 'SS-25110030', caseTypeName: 'New Employee Onboarding', overdueText: '20d overdue', overdueDays: 20, status: 'In Progress', detailUrl: '/cases/detail/SS-25110030' },
      { caseId: 'enc-ss-25110031', caseNumber: 'SS-25110031', caseTypeName: 'Background Check', overdueText: '15d overdue', overdueDays: 15, status: 'Assigned', detailUrl: '/cases/detail/SS-25110031' },
    ],
  },
  {
    userId: 'hr-023', firstName: 'Lisa', lastName: 'Park', role: 'HR Agent',
    email: 'lisa.park@company.gov', phone: '(555) 211-4196', teams: ['Payroll & Compensation'],
    activeCasesCount: 2, capacity: 8, utilizationPercent: 25, overdueCount: 1,
    highPriorityCount: 0, closedThisMonthCount: 0,
    isOverCapacity: false, closedHiddenCount: 0,
    timeOff: null,
    cases: [
      { caseId: 'enc-ss-26050023', caseNumber: 'SS-26050023', caseTypeName: 'Benefits Enrollment', overdueText: '5d overdue', overdueDays: 5, status: 'In Progress', detailUrl: '/cases/detail/SS-26050023' },
    ],
  },
  {
    userId: 'hr-021', firstName: 'Rachel', lastName: 'Turner', role: 'HR Agent',
    email: 'rachel.turner@company.gov', phone: '(555) 212-5207', teams: ['Payroll & Compensation'],
    activeCasesCount: 2, capacity: 8, utilizationPercent: 25, overdueCount: 2,
    highPriorityCount: 0, closedThisMonthCount: 1,
    isOverCapacity: false, closedHiddenCount: 0,
    timeOff: { id: 'timeoff-003', type: 'Sick Leave', startDate: '2026-06-01', endDate: '2026-06-03', durationDays: 3, notes: '' },
    cases: [
      { caseId: 'enc-ss-26050025', caseNumber: 'SS-26050025', caseTypeName: 'Paycheck / Salary Discrepancy', overdueText: '10d overdue', overdueDays: 10, status: 'New', detailUrl: '/cases/detail/SS-26050025' },
      { caseId: 'enc-ss-26050026', caseNumber: 'SS-26050026', caseTypeName: 'Tax Withholding (W-4)', overdueText: '6d overdue', overdueDays: 6, status: 'Assigned', detailUrl: '/cases/detail/SS-26050026' },
    ],
  },
];

const TIME_OFF_MEMBERS: RawMember[] = [
  {
    userId: 'hr-002', firstName: 'David', lastName: 'Kim', role: 'Manager',
    email: 'david.kim@company.gov', phone: '(555) 205-8530', teams: ['Benefits & Leave'],
    activeCasesCount: 5, capacity: 4, utilizationPercent: 125, overdueCount: 5,
    highPriorityCount: 2, closedThisMonthCount: 3,
    isOverCapacity: true, closedHiddenCount: 0,
    timeOff: { id: 'timeoff-001', type: 'Vacation', startDate: '2026-05-22', endDate: '2026-05-24', durationDays: 2, notes: '' },
    cases: OVER_CAPACITY_MEMBERS[4].cases,
  },
  {
    userId: 'hr-003', firstName: 'Jennifer', lastName: 'Martinez', role: 'Manager',
    email: 'jennifer.martinez@company.gov', phone: '(555) 207-0752', teams: ['Payroll & Compensation'],
    activeCasesCount: 3, capacity: 10, utilizationPercent: 30, overdueCount: 3,
    highPriorityCount: 1, closedThisMonthCount: 4,
    isOverCapacity: false, closedHiddenCount: 0,
    timeOff: { id: 'timeoff-002', type: 'Vacation', startDate: '2026-05-22', endDate: '2026-05-24', durationDays: 2, notes: '' },
    cases: OVER_CAPACITY_MEMBERS[6].cases,
  },
  {
    userId: 'hr-016', firstName: 'Olivia', lastName: 'Reyes', role: 'HR Agent',
    email: 'olivia.reyes@company.gov', phone: '(555) 201-4176', teams: ['Payroll & Compensation'],
    activeCasesCount: 3, capacity: 2, utilizationPercent: 150, overdueCount: 3,
    highPriorityCount: 0, closedThisMonthCount: 0,
    isOverCapacity: true, closedHiddenCount: 0,
    timeOff: { id: 'timeoff-010', type: 'Vacation', startDate: '2026-05-26', endDate: '2026-05-30', durationDays: 5, notes: 'Memorial Day week off' },
    cases: OVER_CAPACITY_MEMBERS[0].cases,
  },
  {
    userId: 'hr-017', firstName: 'Nathan', lastName: 'Brooks', role: 'HR Agent',
    email: 'nathan.brooks@company.gov', phone: '(555) 202-5287', teams: ['Payroll & Compensation'],
    activeCasesCount: 3, capacity: 2, utilizationPercent: 150, overdueCount: 3,
    highPriorityCount: 1, closedThisMonthCount: 0,
    isOverCapacity: true, closedHiddenCount: 0,
    timeOff: { id: 'timeoff-011', type: 'Vacation', startDate: '2026-06-08', endDate: '2026-06-12', durationDays: 5, notes: '' },
    cases: OVER_CAPACITY_MEMBERS[1].cases,
  },
  {
    userId: 'hr-018', firstName: 'Sofia', lastName: 'Alvarez', role: 'HR Agent',
    email: 'sofia.alvarez@company.gov', phone: '(555) 203-6398', teams: ['Payroll & Compensation'],
    activeCasesCount: 3, capacity: 2, utilizationPercent: 150, overdueCount: 3,
    highPriorityCount: 0, closedThisMonthCount: 2,
    isOverCapacity: true, closedHiddenCount: 0,
    timeOff: { id: 'timeoff-012', type: 'Vacation', startDate: '2026-06-15', endDate: '2026-06-19', durationDays: 5, notes: '' },
    cases: OVER_CAPACITY_MEMBERS[2].cases,
  },
  {
    userId: 'hr-019', firstName: 'Henry', lastName: 'Walsh', role: 'HR Agent',
    email: 'henry.walsh@company.gov', phone: '(555) 204-7419', teams: ['Payroll & Compensation'],
    activeCasesCount: 3, capacity: 2, utilizationPercent: 150, overdueCount: 3,
    highPriorityCount: 0, closedThisMonthCount: 1,
    isOverCapacity: true, closedHiddenCount: 0,
    timeOff: { id: 'timeoff-013', type: 'Vacation', startDate: '2026-06-22', endDate: '2026-06-22', durationDays: 1, notes: '' },
    cases: OVER_CAPACITY_MEMBERS[3].cases,
  },
  {
    userId: 'hr-021', firstName: 'Rachel', lastName: 'Turner', role: 'HR Agent',
    email: 'rachel.turner@company.gov', phone: '(555) 212-5207', teams: ['Payroll & Compensation'],
    activeCasesCount: 2, capacity: 8, utilizationPercent: 25, overdueCount: 0,
    highPriorityCount: 0, closedThisMonthCount: 1,
    isOverCapacity: false, closedHiddenCount: 0,
    timeOff: { id: 'timeoff-003', type: 'Sick Leave', startDate: '2026-06-01', endDate: '2026-06-03', durationDays: 3, notes: '' },
    cases: [],
  },
  {
    userId: 'hr-022', firstName: 'Carlos', lastName: 'Rivera', role: 'HR Agent',
    email: 'carlos.rivera@company.gov', phone: '(555) 213-6318', teams: ['Payroll & Compensation'],
    activeCasesCount: 3, capacity: 6, utilizationPercent: 50, overdueCount: 1,
    highPriorityCount: 0, closedThisMonthCount: 2,
    isOverCapacity: false, closedHiddenCount: 0,
    timeOff: { id: 'timeoff-004', type: 'Vacation', startDate: '2026-06-29', endDate: '2026-07-03', durationDays: 5, notes: '' },
    cases: [],
  },
];

const SARAH_JOHNSON: RawMember = {
  userId: 'hr-004', firstName: 'Sarah', lastName: 'Johnson', role: 'Manager',
  email: 'sarah.johnson@company.gov', phone: '(555) 214-7429', teams: ['Payroll & Compensation'],
  activeCasesCount: 0, capacity: 20, utilizationPercent: 0, overdueCount: 0,
  highPriorityCount: 0, closedThisMonthCount: 3,
  isOverCapacity: false,
  // closedHiddenCount: 3 — these 3 closed cases are revealed when includeClosedCases is true
  closedHiddenCount: 3,
  timeOff: null,
  cases: [],
};

const THOMAS_BLAKE: RawMember = {
  userId: 'hr-005', firstName: 'Thomas', lastName: 'Blake', role: 'HR Agent',
  email: 'thomas.blake@company.gov', phone: '(555) 209-2974', teams: ['Benefits & Leave'],
  activeCasesCount: 4, capacity: 12, utilizationPercent: 33, overdueCount: 4,
  highPriorityCount: 1, closedThisMonthCount: 2,
  isOverCapacity: false, closedHiddenCount: 0,
  timeOff: null,
  cases: [
    { caseId: 'enc-er-25110001', caseNumber: 'ER-25110001', caseTypeName: 'Resource Management', overdueText: '26d overdue', overdueDays: 26, status: 'In Progress', detailUrl: '/cases/detail/ER-25110001' },
    { caseId: 'enc-er-25110002', caseNumber: 'ER-25110002', caseTypeName: 'Whistleblower Complaint', overdueText: '25d overdue', overdueDays: 25, status: 'In Progress', detailUrl: '/cases/detail/ER-25110002' },
    { caseId: 'enc-er-25110003', caseNumber: 'ER-25110003', caseTypeName: 'Workplace Harassment', overdueText: '18d overdue', overdueDays: 18, status: 'Assigned', detailUrl: '/cases/detail/ER-25110003' },
    { caseId: 'enc-er-25110004', caseNumber: 'ER-25110004', caseTypeName: 'Disciplinary Action', overdueText: '12d overdue', overdueDays: 12, status: 'New', detailUrl: '/cases/detail/ER-25110004' },
  ],
};

const REBECCA_CHEN: RawMember = {
  userId: 'hr-006', firstName: 'Rebecca', lastName: 'Chen', role: 'Manager',
  email: 'rebecca.chen@company.gov', phone: '(555) 215-8540', teams: ['Payroll & Compensation'],
  activeCasesCount: 5, capacity: 5, utilizationPercent: 100, overdueCount: 0,
  highPriorityCount: 1, closedThisMonthCount: 2,
  isOverCapacity: false, closedHiddenCount: 0,
  timeOff: null,
  cases: [
    { caseId: 'enc-ss-26050031', caseNumber: 'SS-26050031', caseTypeName: 'Benefits Enrollment', overdueText: 'On time', overdueDays: 0, status: 'In Progress', detailUrl: '/cases/detail/SS-26050031' },
    { caseId: 'enc-ss-26050032', caseNumber: 'SS-26050032', caseTypeName: 'Paycheck / Salary Discrepancy', overdueText: 'On time', overdueDays: 0, status: 'New', detailUrl: '/cases/detail/SS-26050032' },
    { caseId: 'enc-ss-26050033', caseNumber: 'SS-26050033', caseTypeName: 'General HR Question', overdueText: 'On time', overdueDays: 0, status: 'Assigned', detailUrl: '/cases/detail/SS-26050033' },
    { caseId: 'enc-ss-26050034', caseNumber: 'SS-26050034', caseTypeName: 'FMLA / Leave of Absence', overdueText: 'On time', overdueDays: 0, status: 'In Progress', detailUrl: '/cases/detail/SS-26050034' },
    { caseId: 'enc-ss-26050035', caseNumber: 'SS-26050035', caseTypeName: 'New Employee Onboarding', overdueText: 'On time', overdueDays: 0, status: 'In Progress', detailUrl: '/cases/detail/SS-26050035' },
  ],
};

const TEAM_WORKLOADS: RawTeam[] = [
  {
    teamId: 'team-benefits', teamName: 'Benefits & Leave',
    managedBy: ['David Kim'],
    overCapacityCount: 1, overdueCount: 9, onTimeOffCount: 1, totalMembers: 2,
    members: [OVER_CAPACITY_MEMBERS[4], THOMAS_BLAKE],
  },
  {
    teamId: 'team-payroll', teamName: 'Payroll & Compensation',
    managedBy: ['Jennifer Martinez', 'Sarah Johnson'],
    overCapacityCount: 6, overdueCount: 29, onTimeOffCount: 7, totalMembers: 11,
    members: [
      OVER_CAPACITY_MEMBERS[6],
      SARAH_JOHNSON,
      OVER_CAPACITY_MEMBERS[0],
      OVER_CAPACITY_MEMBERS[1],
      OVER_CAPACITY_MEMBERS[2],
      OVER_CAPACITY_MEMBERS[3],
      OVER_CAPACITY_MEMBERS[5],
      REBECCA_CHEN,
      TIME_OFF_MEMBERS[6],
      TIME_OFF_MEMBERS[7],
      {
        userId: 'hr-023', firstName: 'Lisa', lastName: 'Park', role: 'HR Agent',
        email: 'lisa.park@company.gov', phone: '(555) 211-4196', teams: ['Payroll & Compensation'],
        activeCasesCount: 2, capacity: 8, utilizationPercent: 25, overdueCount: 1,
        highPriorityCount: 0, closedThisMonthCount: 0,
        isOverCapacity: false, closedHiddenCount: 0,
        timeOff: null,
        cases: [
          { caseId: 'enc-ss-26050023', caseNumber: 'SS-26050023', caseTypeName: 'Benefits Enrollment', overdueText: '5d overdue', overdueDays: 5, status: 'In Progress', detailUrl: '/cases/detail/SS-26050023' },
          { caseId: 'enc-ss-26050024', caseNumber: 'SS-26050024', caseTypeName: 'Paycheck / Salary Discrepancy', overdueText: 'On time', overdueDays: 0, status: 'New', detailUrl: '/cases/detail/SS-26050024' },
        ],
      },
    ],
  },
  {
    teamId: 'team-records', teamName: 'Records & Onboarding',
    managedBy: ['Maria Santos'],
    overCapacityCount: 0, overdueCount: 14, onTimeOffCount: 0, totalMembers: 2,
    members: [
      {
        userId: 'hr-001', firstName: 'Maria', lastName: 'Santos', role: 'Manager',
        email: 'maria.santos@company.gov', phone: '(555) 208-1863', teams: ['Records & Onboarding'],
        activeCasesCount: 10, capacity: 10, utilizationPercent: 100, overdueCount: 10,
        highPriorityCount: 3, closedThisMonthCount: 5,
        isOverCapacity: false, closedHiddenCount: 0,
        timeOff: null,
        cases: [
          { caseId: 'enc-ss-25110010', caseNumber: 'SS-25110010', caseTypeName: 'General HR Question', overdueText: '42d overdue', overdueDays: 42, status: 'Assigned', detailUrl: '/cases/detail/SS-25110010' },
          { caseId: 'enc-ss-25110011', caseNumber: 'SS-25110011', caseTypeName: 'FMLA / Leave of Absence', overdueText: '41d overdue', overdueDays: 41, status: 'Assigned', detailUrl: '/cases/detail/SS-25110011' },
          { caseId: 'enc-ss-25110013', caseNumber: 'SS-25110013', caseTypeName: 'Paycheck / Salary Discrepancy', overdueText: '35d overdue', overdueDays: 35, status: 'In Progress', detailUrl: '/cases/detail/SS-25110013' },
        ],
      },
      {
        userId: 'hr-007', firstName: 'James', lastName: 'Wilson', role: 'HR Agent',
        email: 'james.wilson@company.gov', phone: '(555) 210-3085', teams: ['Records & Onboarding'],
        activeCasesCount: 4, capacity: 8, utilizationPercent: 50, overdueCount: 4,
        highPriorityCount: 2, closedThisMonthCount: 1,
        isOverCapacity: false, closedHiddenCount: 0,
        timeOff: null,
        cases: [
          { caseId: 'enc-ss-25110030', caseNumber: 'SS-25110030', caseTypeName: 'New Employee Onboarding', overdueText: '20d overdue', overdueDays: 20, status: 'In Progress', detailUrl: '/cases/detail/SS-25110030' },
          { caseId: 'enc-ss-25110031', caseNumber: 'SS-25110031', caseTypeName: 'Background Check', overdueText: '15d overdue', overdueDays: 15, status: 'Assigned', detailUrl: '/cases/detail/SS-25110031' },
        ],
      },
    ],
  },
];

// Proposal member constants
const DAVID_KIM: RawProposalMember = { userId: 'hr-002', displayName: 'David Kim', currentCases: 10, capacity: 4, utilizationPercent: 250 };
const THOMAS_BLAKE_PM: RawProposalMember = { userId: 'hr-005', displayName: 'Thomas Blake', currentCases: 1, capacity: 12, utilizationPercent: 8 };
const MARIA_SANTOS: RawProposalMember = { userId: 'hr-001', displayName: 'Maria Santos', currentCases: 15, capacity: 10, utilizationPercent: 150 };
const OLIVIA_REYES: RawProposalMember = { userId: 'hr-016', displayName: 'Olivia Reyes', currentCases: 3, capacity: 2, utilizationPercent: 150 };
const SARAH_JOHNSON_PM: RawProposalMember = { userId: 'hr-006', displayName: 'Sarah Johnson', currentCases: 1, capacity: 20, utilizationPercent: 5 };
const JENNIFER_MARTINEZ: RawProposalMember = { userId: 'hr-003', displayName: 'Jennifer Martinez', currentCases: 3, capacity: 10, utilizationPercent: 30 };

const REBALANCING_PROPOSALS: RawProposal[] = [
  // Benefits & Leave — from David Kim (over capacity) to Thomas Blake
  {
    caseId: 'enc-ss-26010002', caseNumber: 'SS-26010002', caseTypeName: 'Direct Deposit Update',
    caseStatusLabel: 'Assigned', isOverdue: true,
    teamId: 'team-benefits', teamName: 'Benefits & Leave',
    fromMember: DAVID_KIM, toMember: THOMAS_BLAKE_PM, alternates: [],
    rationale: 'David Kim is over capacity (250%) and on planned leave. Thomas Blake has capacity at 8%.',
  },
  {
    caseId: 'enc-ss-25110022', caseNumber: 'SS-25110022', caseTypeName: 'Medical / Dental / Vision',
    caseStatusLabel: 'Assigned', isOverdue: true,
    teamId: 'team-benefits', teamName: 'Benefits & Leave',
    fromMember: DAVID_KIM, toMember: THOMAS_BLAKE_PM, alternates: [],
    rationale: 'David Kim is over capacity (250%) and on leave. Thomas Blake has bandwidth.',
  },
  {
    caseId: 'enc-ss-25110012', caseNumber: 'SS-25110012', caseTypeName: 'General HR Question',
    caseStatusLabel: 'Assigned', isOverdue: true,
    teamId: 'team-benefits', teamName: 'Benefits & Leave',
    fromMember: DAVID_KIM, toMember: THOMAS_BLAKE_PM, alternates: [],
    rationale: 'David Kim is over capacity. Thomas Blake has 25% utilization and availability.',
  },
  // Payroll & Compensation — from Olivia Reyes with alternates
  {
    caseId: 'enc-ss-26050003', caseNumber: 'SS-26050003', caseTypeName: 'Direct Deposit Update',
    caseStatusLabel: 'Assigned', isOverdue: true,
    teamId: 'team-payroll', teamName: 'Payroll & Compensation',
    fromMember: OLIVIA_REYES, toMember: SARAH_JOHNSON_PM,
    alternates: [THOMAS_BLAKE_PM, JENNIFER_MARTINEZ],
    rationale: 'Olivia Reyes is at 150% capacity with upcoming vacation. Sarah Johnson has no active assignments.',
  },
  {
    caseId: 'enc-ss-26050002', caseNumber: 'SS-26050002', caseTypeName: 'Tax Withholding (W-4)',
    caseStatusLabel: 'In Progress', isOverdue: true,
    teamId: 'team-payroll', teamName: 'Payroll & Compensation',
    fromMember: OLIVIA_REYES, toMember: THOMAS_BLAKE_PM,
    alternates: [SARAH_JOHNSON_PM, JENNIFER_MARTINEZ],
    rationale: 'Olivia Reyes is over capacity. Thomas Blake has low workload and can handle immediately.',
  },
  {
    caseId: 'enc-ss-26050001', caseNumber: 'SS-26050001', caseTypeName: 'Paycheck / Salary Discrepancy',
    caseStatusLabel: 'In Progress', isOverdue: true,
    teamId: 'team-payroll', teamName: 'Payroll & Compensation',
    fromMember: OLIVIA_REYES, toMember: JENNIFER_MARTINEZ,
    alternates: [SARAH_JOHNSON_PM, THOMAS_BLAKE_PM],
    rationale: 'Olivia Reyes is at 150% capacity. Jennifer Martinez has capacity at 30%.',
  },
  // Records & Onboarding — from Maria Santos to Thomas Blake
  {
    caseId: 'enc-ss-25120002', caseNumber: 'SS-25120002', caseTypeName: 'Health Insurance Enrollment Question',
    caseStatusLabel: 'Assigned', isOverdue: true,
    teamId: 'team-records', teamName: 'Records & Onboarding',
    fromMember: MARIA_SANTOS, toMember: THOMAS_BLAKE_PM, alternates: [],
    rationale: 'Maria Santos is over capacity (150%). Thomas Blake has bandwidth to absorb this case.',
  },
  {
    caseId: 'enc-ss-25120008', caseNumber: 'SS-25120008', caseTypeName: 'Bereavement Leave Request',
    caseStatusLabel: 'Assigned', isOverdue: true,
    teamId: 'team-records', teamName: 'Records & Onboarding',
    fromMember: MARIA_SANTOS, toMember: THOMAS_BLAKE_PM, alternates: [],
    rationale: 'Maria Santos is over capacity. Thomas Blake can handle this case.',
  },
  {
    caseId: 'enc-ss-25110007', caseNumber: 'SS-25110007', caseTypeName: 'Retirement Plan',
    caseStatusLabel: 'Assigned', isOverdue: true,
    teamId: 'team-records', teamName: 'Records & Onboarding',
    fromMember: MARIA_SANTOS, toMember: THOMAS_BLAKE_PM, alternates: [],
    rationale: 'Maria Santos is over capacity (150%). Thomas Blake has low utilization (8%).',
  },
];

function _resetClosedHiddenCount(member: RawMember): RawMember {
  if (member.closedHiddenCount > 0) {
    return { ...member, closedHiddenCount: 0 };
  }
  return member;
}

@Injectable({ providedIn: 'root' })
export class TeamWorkloadMockRepository {
  getSummary(_viewMode: WorkloadViewMode): Observable<TeamWorkloadSummaryEntity> {
    const e = new TeamWorkloadSummaryEntity();
    e.overCapacityCount = 7;
    e.overdueCasesCount = 44;
    e.atRiskCasesCount = 1;
    e.plannedTimeOffCount = 8;
    return of(e).pipe(delay(200));
  }

  getOverCapacityMembers(_viewMode: WorkloadViewMode): Observable<WorkloadMemberEntity[]> {
    return of(OVER_CAPACITY_MEMBERS).pipe(delay(300), map((arr) => arr.map(_mapMember)));
  }

  getOverdueCasesMembers(_viewMode: WorkloadViewMode): Observable<WorkloadMemberEntity[]> {
    return of(OVERDUE_MEMBERS).pipe(delay(300), map((arr) => arr.map(_mapMember)));
  }

  getAtRiskMembers(_viewMode: WorkloadViewMode): Observable<WorkloadMemberEntity[]> {
    return of([REBECCA_CHEN]).pipe(delay(200), map((arr) => arr.map(_mapMember)));
  }

  getTimeOffMembers(_viewMode: WorkloadViewMode): Observable<WorkloadMemberEntity[]> {
    return of(TIME_OFF_MEMBERS).pipe(delay(300), map((arr) => arr.map(_mapMember)));
  }

  getTeamWorkloads(_viewMode: WorkloadViewMode, includeClosedCases: boolean): Observable<TeamWorkloadEntity[]> {
    if (!includeClosedCases) {
      return of(TEAM_WORKLOADS).pipe(delay(400), map((arr) => arr.map(_mapTeam)));
    }

    // When showing closed cases, reset closedHiddenCount for members who have them
    const withClosedCases = TEAM_WORKLOADS.map((team) => ({
      ...team,
      members: team.members.map(_resetClosedHiddenCount),
    }));

    return of(withClosedCases).pipe(delay(400), map((arr) => arr.map(_mapTeam)));
  }

  getRebalancingProposals(): Observable<RebalancingProposalEntity[]> {
    return of(REBALANCING_PROPOSALS).pipe(delay(2500), map((arr) => arr.map(_mapProposal)));
  }

  getTeamBalancingCandidates(): Observable<TeamBalancingCandidateEntity[]> {
    const candidates = TEAM_WORKLOADS.map(t => {
      const c = new TeamBalancingCandidateEntity();
      c.teamId = t.teamId;
      c.teamName = t.teamName;
      c.overCapacityCount = t.overCapacityCount;
      c.overdueCount = t.overdueCount;
      return c;
    });
    return of(candidates).pipe(delay(300));
  }

  generateRebalancingProposals(teamIds: string[]): Observable<RebalancingProposalGroupEntity[]> {
    const filtered = REBALANCING_PROPOSALS.filter(p => teamIds.includes(p.teamId));
    const mapped = filtered.map(_mapProposal);
    const grouped = _groupProposalsByTeam(mapped);
    return of(grouped).pipe(delay(2500));
  }

  submitProposalFeedback(_cmd: { caseId: string; reason: ProposalRemovalReason; note: string | null }): Observable<void> {
    return of(void 0).pipe(delay(150));
  }

  applyRebalancingProposals(cmd: { reassignments: { caseId: string; fromUserId: string; toUserId: string }[] }): Observable<{ appliedCount: number }> {
    return of({ appliedCount: cmd.reassignments.length }).pipe(delay(400));
  }
}
